import { useState, useMemo } from 'react';
import { cn } from '@/lib/cn';
import { toast } from 'sonner';
import type { ResolvedEntityInfo } from '@shared/types/api';

// Helper to format diff values with dynamic name/email resolution
export function FormattedDiffValue({
  val,
  resolvedEntities,
}: {
  val: unknown;
  resolvedEntities?: Record<string, ResolvedEntityInfo>;
}) {
  if (val === undefined || val === null) {
    return <span className="italic text-gray-400">&lt;none&gt;</span>;
  }

  if (typeof val === 'string') {
    const info = resolvedEntities?.[val];
    if (info) {
      return (
        <div className="flex flex-col gap-1">
          <span className="font-mono text-[11px] select-all">"{val}"</span>
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-white border border-gray-200 text-[11px] font-sans font-medium text-gray-800 shadow-2xs w-fit">
            <span className="text-emerald-700 font-bold">↳</span>
            <span className="font-semibold">{info.label}</span>
            {info.subtext && <span className="text-gray-500 font-normal">({info.subtext})</span>}
            {info.type && (
              <span className="text-[9px] uppercase px-1 py-0.2 rounded bg-gray-100 text-gray-600 font-bold">
                {info.type}
              </span>
            )}
          </div>
        </div>
      );
    }
  }

  if (Array.isArray(val)) {
    const hasResolved = val.some((item) => typeof item === 'string' && resolvedEntities?.[item]);
    if (hasResolved) {
      return (
        <div className="flex flex-col gap-1.5">
          {val.map((item, idx) => {
            const isStr = typeof item === 'string';
            const info = isStr && resolvedEntities ? resolvedEntities[item] : null;
            return (
              <div key={idx} className="flex flex-col gap-0.5 border-b border-gray-200/50 pb-1.5 last:border-0 last:pb-0">
                <span className="font-mono text-[11px] select-all">"{item}"</span>
                {info && (
                  <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-white border border-gray-200 text-[11px] font-sans font-medium text-gray-800 shadow-2xs w-fit">
                    <span className="text-emerald-700 font-bold">↳</span>
                    <span className="font-semibold">{info.label}</span>
                    {info.subtext && <span className="text-gray-500 font-normal">({info.subtext})</span>}
                    {info.type && (
                      <span className="text-[9px] uppercase px-1 py-0.2 rounded bg-gray-100 text-gray-600 font-bold">
                        {info.type}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }
  }

  return <pre className="whitespace-pre-wrap font-mono text-[11px]">{JSON.stringify(val, null, 2)}</pre>;
}

// JSON Diff inspector component
export function JsonDiffViewer({
  before,
  after,
  resolvedEntities,
}: {
  before: unknown;
  after: unknown;
  resolvedEntities?: Record<string, ResolvedEntityInfo>;
}) {
  const [activeTab, setActiveTab] = useState<'diff' | 'after' | 'before'>('diff');

  const beforeObj = before && typeof before === 'object' ? (before as Record<string, unknown>) : null;
  const afterObj = after && typeof after === 'object' ? (after as Record<string, unknown>) : null;

  const diffKeys = useMemo(() => {
    if (!beforeObj && !afterObj) return [];
    const allKeys = new Set([
      ...(beforeObj ? Object.keys(beforeObj) : []),
      ...(afterObj ? Object.keys(afterObj) : []),
    ]);
    return Array.from(allKeys).sort();
  }, [beforeObj, afterObj]);

  const hasChanges = diffKeys.some((key) => {
    const bVal = beforeObj ? JSON.stringify(beforeObj[key]) : undefined;
    const aVal = afterObj ? JSON.stringify(afterObj[key]) : undefined;
    return bVal !== aVal;
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between border-b border-gray-200 pb-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setActiveTab('diff')}
            className={cn(
              'px-3 py-1.5 text-xs font-semibold rounded-md transition-colors',
              activeTab === 'diff'
                ? 'bg-brand-900 text-white'
                : 'text-gray-600 hover:bg-gray-100',
            )}
          >
            Changes Diff {hasChanges && <span className="ml-1 text-[10px] text-amber-300">●</span>}
          </button>
        </div>
        <button
          type="button"
          onClick={() => {
            const dataToCopy =
              activeTab === 'diff'
                ? JSON.stringify({ before, after }, null, 2)
                : activeTab === 'after'
                  ? JSON.stringify(after, null, 2)
                  : JSON.stringify(before, null, 2);
            navigator.clipboard.writeText(dataToCopy);
            toast.success('JSON copied to clipboard');
          }}
          className="text-xs text-brand-700 hover:text-brand-900 font-medium inline-flex items-center gap-1"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          Copy JSON
        </button>
      </div>

      {activeTab === 'diff' && (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {!beforeObj && !afterObj ? (
            <p className="text-xs text-gray-500 italic p-3">No state diff recorded for this action.</p>
          ) : !hasChanges ? (
            <p className="text-xs text-gray-500 italic p-3">No field modifications detected between before and after.</p>
          ) : (
            <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 overflow-hidden text-xs">
              {diffKeys.map((key) => {
                const bVal = beforeObj ? beforeObj[key] : undefined;
                const aVal = afterObj ? afterObj[key] : undefined;
                const isDiff = JSON.stringify(bVal) !== JSON.stringify(aVal);
                if (!isDiff) return null;

                return (
                  <div key={key} className="p-2.5 bg-gray-50/50 flex flex-col gap-1.5">
                    <span className="font-mono font-semibold text-gray-900">{key}</span>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div className="rounded bg-rose-50 border border-rose-200 p-2 text-rose-900 font-mono text-[11px] overflow-x-auto">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-rose-600 block mb-1">
                          Before
                        </span>
                        <FormattedDiffValue val={bVal} resolvedEntities={resolvedEntities} />
                      </div>
                      <div className="rounded bg-emerald-50 border border-emerald-200 p-2 text-emerald-900 font-mono text-[11px] overflow-x-auto">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 block mb-1">
                          After
                        </span>
                        <FormattedDiffValue val={aVal} resolvedEntities={resolvedEntities} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'after' && (
        <pre className="max-h-96 overflow-y-auto rounded-lg bg-gray-900 p-3.5 text-emerald-400 font-mono text-xs">
          {after ? JSON.stringify(after, null, 2) : '// No after snapshot recorded'}
        </pre>
      )}

      {activeTab === 'before' && (
        <pre className="max-h-96 overflow-y-auto rounded-lg bg-gray-900 p-3.5 text-amber-400 font-mono text-xs">
          {before ? JSON.stringify(before, null, 2) : '// No before snapshot recorded'}
        </pre>
      )}
    </div>
  );
}
